import type {FC} from 'react'
import {useMemo} from 'react'
import {ActionList, ActionMenu} from '@primer/react'
import {GitBranchIcon} from '@primer/octicons-react'
import type {Repository} from '@github-ui/current-repository'
import {RefSelector} from '@github-ui/ref-selector'
import {ReposSelector} from '@github-ui/repos-selector'
import {TimeFilter} from '@github-ui/action-menu-selector/TimeFilter'
import type {InsightsFilter, Ruleset, SourceType} from '../../types/rules-types'
import type {Organization} from '@github-ui/repos-types'
import {RuleStatusFilter} from './RuleStatusFilter'
import {UserSelector} from '@github-ui/user-selector'
import {RulesetFilter} from './RulesetFilter'
import {insightsIndexPath} from '../../helpers/insights-filter'
import {useInsightsActors} from '../../hooks/use-insights-actors'
import {useRelativeNavigation} from '../../hooks/use-relative-navigation'
import {EvaluateStatusFilter} from './EvaluateStatusFilter'
import {OrgSelector, simpleOrgLoader} from '@github-ui/org-selector'
import {getRepoSuggestionsForOrg} from '../../services/api'
import {useIsStafftools} from '../../hooks/use-is-stafftools'

import styles from './InsightsFilterBar.module.css'
import {clsx} from 'clsx'

export type InsightsFilterProps = {
  filter: InsightsFilter
  source: Repository | Organization
  sourceType: SourceType
  branchListCacheKey?: string
  rulesets: Ruleset[]
  organizations?: string[]
}

export const InsightsFilterBar: FC<InsightsFilterProps> = ({
  filter,
  source,
  sourceType,
  branchListCacheKey,
  rulesets,
  organizations,
}) => {
  const isStafftools = useIsStafftools()
  const {actor, timePeriod, ruleset, branch, ruleStatus, evaluateStatus, repository, organization} = filter
  const {navigate} = useRelativeNavigation()
  const insightsActorsState = useInsightsActors()

  const updateFilter = (newFilter: InsightsFilter) => {
    newFilter.page = undefined // Reset to first page when filter changes
    navigate('.', insightsIndexPath({filter: newFilter}), true)
  }

  const {resolvePath} = useRelativeNavigation()
  const queryForRepos = async (query: string) => {
    return (
      await getRepoSuggestionsForOrg(resolvePath('repo_suggestions'), {
        query,
        excludePublicRepos: false,
      })
    ).map(r => ({name: r.name}))
  }

  const namedOrgs = useMemo(() => organizations?.map(o => ({name: o})) || [], [organizations])

  return (
    <div className={clsx('pr-mb-3', styles.InsightsFilterBarContainer)}>
      <div className="d-flex p-1">
        <EvaluateStatusFilter
          currentEvaluateStatus={evaluateStatus || 'active'}
          onSelect={selectedEvaluateStatus => {
            if (selectedEvaluateStatus !== evaluateStatus) {
              updateFilter({...filter, evaluateStatus: selectedEvaluateStatus})
            }
          }}
        />
      </div>
      <div className="d-flex p-1">
        <RulesetFilter
          currentRuleset={ruleset}
          allRulesets={rulesets}
          onSelect={selectedRuleset => {
            if (selectedRuleset !== ruleset) {
              updateFilter({...filter, ruleset: selectedRuleset})
            }
          }}
        />
      </div>
      {sourceType === 'repository' && (
        <div className="d-flex p-1">
          {!isStafftools ? (
            <RefSelector
              currentCommitish={branch || 'All branches'}
              defaultBranch={(source as Repository).defaultBranch}
              owner={(source as Repository).ownerLogin}
              repo={source.name}
              canCreate={false}
              cacheKey={branchListCacheKey as string}
              onSelectItem={ref => {
                if (ref !== branch) {
                  updateFilter({...filter, branch: ref})
                }
              }}
              hotKey="w"
              types={['branch']}
              hideShowAll
              customFooterItemProps={{
                text: 'View for all branches',
                onClick: () => {
                  if (branch !== undefined) {
                    updateFilter({...filter, branch: undefined})
                  }
                },
                sx: {alignItems: 'center', display: 'flex', justifyContent: 'center'},
              }}
            />
          ) : (
            <StafftoolsBranchesMenu />
          )}
        </div>
      )}
      {sourceType === 'organization' && (
        <div className="d-flex p-1">
          <ReposSelector
            currentSelection={repository ? {name: repository} : undefined}
            repositoryLoader={queryForRepos}
            selectAllOption
            selectionVariant="single"
            onSelect={selectedRepository => {
              if (selectedRepository?.name !== repository) {
                updateFilter({...filter, repository: selectedRepository?.name})
              }
            }}
          />
        </div>
      )}
      {sourceType === 'enterprise' && (
        <div className="d-flex p-1">
          <OrgSelector
            selectionVariant="single"
            buttonText={organization ? organization : 'All organizations'}
            selection={organization ? {name: organization} : undefined}
            selectOrg={org => {
              if (org.name !== organization) {
                updateFilter({...filter, organization: org.name})
              }
            }}
            removeOrg={() => updateFilter({...filter, organization: undefined})}
            orgLoader={simpleOrgLoader(namedOrgs)}
          />
        </div>
      )}
      <div className="d-flex p-1">
        <UserSelector
          defaultText="All users"
          usersState={insightsActorsState}
          currentUser={actor}
          onSelect={selectedUser => {
            if (selectedUser !== actor) {
              updateFilter({...filter, actor: selectedUser})
            }
          }}
          renderCustomFooter={() => (
            <ActionList role="none" className="border-top border-color-border-default">
              <ActionList.Item
                role="button"
                onSelect={() => {
                  updateFilter({...filter, actor: undefined})
                }}
              >
                View for all users
              </ActionList.Item>
            </ActionList>
          )}
          width="medium"
        />
      </div>
      <div className="d-flex p-1">
        <TimeFilter
          currentTimePeriod={timePeriod || 'day'}
          onSelect={selectedTimePeriod => {
            if (selectedTimePeriod !== timePeriod) {
              updateFilter({...filter, timePeriod: selectedTimePeriod})
            }
          }}
        />
      </div>
      <div className="d-flex p-1">
        <RuleStatusFilter
          currentRuleStatus={ruleStatus || 'all'}
          onSelect={selectedRuleStatus => {
            if (selectedRuleStatus !== ruleStatus) {
              updateFilter({...filter, ruleStatus: selectedRuleStatus})
            }
          }}
        />
      </div>
    </div>
  )
}

const StafftoolsBranchesMenu = () => (
  <ActionMenu>
    <ActionMenu.Button size="medium" leadingVisual={GitBranchIcon}>
      All branches
    </ActionMenu.Button>
    <ActionMenu.Overlay width="auto">
      <ActionList>
        <ActionList.Item disabled aria-label="Branches are unavailable in stafftools">
          Branches are unavailable in stafftools
        </ActionList.Item>
      </ActionList>
    </ActionMenu.Overlay>
  </ActionMenu>
)
